import { ApiService, type ApiRequestConfig } from "./api.service";
import { uploadToPresignedUrl, type PresignedUploadOptions } from "./presigned-upload";
import type { TIssueAttachment, TIssueAttachmentUploadResponse } from "@/types/issue-attachment";

export class IssueAttachmentService extends ApiService {
  // Attachments on a work item. A *project* work item must use the
  // project-scoped endpoint (the workspace-scoped one rejects project items so
  // project RBAC stays authoritative); a *project-less* one uses the
  // workspace-scoped path. Mirrors workspaceSkillService.issueSkillsPath and
  // workItemService.subWorkItemsPath — without the branch, a project-less event
  // (project_id null → "") builds `…/projects//work-items/…` and 404s, since
  // `<uuid:project_id>` cannot match an empty segment, which is why the
  // Attachments panel could neither list nor upload on one (NG-2016).
  private attachmentsPath(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
  ): string {
    return projectId
      ? `/api/assets/v2/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/attachments/`
      : `/api/assets/v2/workspaces/${slug}/work-items/${issueId}/attachments/`;
  }

  async listAttachments(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    config?: ApiRequestConfig,
  ): Promise<TIssueAttachment[]> {
    return this.get(this.attachmentsPath(slug, projectId, issueId), config);
  }

  async upload(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    file: File,
    options?: PresignedUploadOptions,
  ): Promise<TIssueAttachment> {
    const path = this.attachmentsPath(slug, projectId, issueId);
    const { upload_data, asset_id } = await this.post<TIssueAttachmentUploadResponse>(path, {
      name: file.name,
      size: file.size,
      content_type: file.type,
    });

    await uploadToPresignedUrl(upload_data, file, options);

    return this.patch(`${path}${asset_id}/`, {});
  }

  async deleteAttachment(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    attachmentId: string,
  ): Promise<void> {
    return this.delete(`${this.attachmentsPath(slug, projectId, issueId)}${attachmentId}/`);
  }
}
